import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import PinataGame from '../components/PinataGame';
import { saveUserEntry, getSettings } from '../utils/db';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { playWinFanfare, playTickSound } from '../utils/soundEffects';
import { useGameExitRoute } from '../utils/navigation';

const { FiGift, FiAward, FiUser, FiPhone, FiBookOpen, FiHome, FiSun, FiMoon, FiArrowRight, FiRotateCcw, FiCheckCircle } = FiIcons;

const PINATA_REWARDS = [
  "⭐ Super Student Champion Badge",
  "✏️ Cartoon Stationary Art Kit",
  "🎨 Rainbow Sketch & Marker Set",
  "🍫 Kids Choco Delight Bar",
  "🎒 School Bag Glow Keychain",
  "💧 Eco Kids Sipper Water Bottle",
  "🎖️ Golden Star Medallion"
];

const PinataFlow = () => {
  const { exitRoute, exitTitle } = useGameExitRoute();
  const [step, setStep] = useState('form'); // 'form' | 'game' | 'result'
  const [formData, setFormData] = useState({ name: '', classGrade: '', mobile: '' });
  const [wonPrize, setWonPrize] = useState('');
  const [couponCode, setCouponCode] = useState('');
  const [isDarkMode, setIsDarkMode] = useState(false); // Day mode default for colorful stalls
  const settings = getSettings();

  const handleStartGame = (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      alert("Please enter the student's name!");
      return;
    }
    const prize = PINATA_REWARDS[Math.floor(Math.random() * PINATA_REWARDS.length)];
    setWonPrize(prize);
    setStep('game');
    playTickSound(800, 0.05);
  };

  const handleGameComplete = (reward) => {
    const code = 'PIN-' + Math.random().toString(36).substring(2, 7).toUpperCase();
    setCouponCode(code);

    const entryData = {
      name: formData.name.trim(),
      mobile: formData.mobile.trim() || 'N/A (Student)',
      classGrade: formData.classGrade.trim() || 'Junior',
      reward: reward,
      couponCode: code,
      gameType: 'Candy Piñata'
    };

    saveUserEntry(entryData);
    setStep('result');
    playWinFanfare();
  };

  const handlePlayAgain = () => {
    setFormData({ name: '', classGrade: '', mobile: '' });
    setWonPrize('');
    setCouponCode('');
    setStep('form');
  };

  return (
    <div className={`min-h-screen transition-colors duration-500 flex flex-col justify-between p-4 md:p-6 ${
      isDarkMode 
        ? 'bg-slate-950 text-slate-100' 
        : 'bg-gradient-to-br from-pink-50 via-purple-50 to-amber-50 text-slate-900'
    }`}>
      {/* Top Header */}
      <header className="flex items-center justify-between pb-4 max-w-4xl mx-auto w-full border-b border-pink-200 dark:border-slate-800">
        <div className="flex items-center gap-3">
          <Link to={exitRoute} title={exitTitle} className={`p-2.5 rounded-full border transition-all ${
            isDarkMode ? 'bg-slate-900 border-slate-700 text-slate-300' : 'bg-white border-pink-200 text-slate-700 shadow-sm'
          }`}>
            <SafeIcon icon={FiHome} className="text-xl" />
          </Link>
          <div>
            <div className="flex items-center gap-2">
              <span className="text-2xl">🪅</span>
              <h1 className="text-xl md:text-2xl font-black bg-clip-text text-transparent bg-gradient-to-r from-pink-600 to-purple-600">
                Candy Piñata Bash
              </h1>
            </div>
            <p className="text-[11px] font-bold text-slate-400 uppercase tracking-widest">
              Kids Stall Tablet Kiosk • Tap & Smash for Prizes!
            </p>
          </div>
        </div>

        {/* Day/Night Toggle */}
        <button 
          onClick={() => setIsDarkMode(!isDarkMode)}
          className={`px-3 py-1.5 rounded-full text-xs font-bold border flex items-center gap-1.5 transition-all cursor-pointer ${
            isDarkMode ? 'bg-amber-400 text-slate-950 border-amber-300 shadow-md' : 'bg-white text-slate-800 border-pink-200 shadow-sm'
          }`}
        >
          <SafeIcon icon={isDarkMode ? FiSun : FiMoon} />
          <span>{isDarkMode ? 'Day ☀️' : 'Night 🌙'}</span>
        </button>
      </header>

      {/* Main Flow Content */}
      <main className="flex-1 flex flex-col items-center justify-center my-6 max-w-lg mx-auto w-full">
        <AnimatePresence mode="wait">
          {step === 'form' && (
            <motion.div
              key="form"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className={`w-full p-6 sm:p-8 rounded-3xl border shadow-xl ${
                isDarkMode ? 'bg-slate-900/90 border-slate-700' : 'bg-white/95 border-pink-100 shadow-pink-200/50'
              }`}
            >
              <div className="text-center mb-6">
                <span className="text-5xl inline-block mb-2 animate-bounce">🪅</span>
                <h2 className="text-2xl font-black text-slate-900 dark:text-white">
                  Welcome Young Explorer!
                </h2>
                <p className="text-xs text-slate-500 mt-1">
                  Enter student details to play the Piñata smash game!
                </p>
              </div>

              <form onSubmit={handleStartGame} className="space-y-4">
                <div>
                  <label className="block text-xs font-black uppercase tracking-wider text-slate-500 mb-1.5 flex items-center gap-1.5">
                    <SafeIcon icon={FiUser} className="text-pink-500" />
                    <span>Student / Child Name *</span>
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.name}
                    onChange={e => setFormData({ ...formData, name: e.target.value })}
                    placeholder="e.g. Aarav Sharma"
                    className="w-full px-4 py-3 rounded-2xl border text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-pink-500 bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-700 text-slate-900 dark:text-white"
                  />
                </div>

                <div>
                  <label className="block text-xs font-black uppercase tracking-wider text-slate-500 mb-1.5 flex items-center gap-1.5">
                    <SafeIcon icon={FiBookOpen} className="text-purple-500" />
                    <span>Class / Grade (Optional)</span>
                  </label>
                  <input
                    type="text"
                    value={formData.classGrade}
                    onChange={e => setFormData({ ...formData, classGrade: e.target.value })}
                    placeholder="e.g. Grade 4 - A"
                    className="w-full px-4 py-3 rounded-2xl border text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-pink-500 bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-700 text-slate-900 dark:text-white"
                  />
                </div>

                <div>
                  <label className="block text-xs font-black uppercase tracking-wider text-slate-500 mb-1.5 flex items-center gap-1.5">
                    <SafeIcon icon={FiPhone} className="text-emerald-500" />
                    <span>Parent Phone (Optional for kids)</span>
                  </label>
                  <input
                    type="tel"
                    value={formData.mobile}
                    onChange={e => setFormData({ ...formData, mobile: e.target.value })}
                    placeholder="e.g. 9876543210 (Optional)"
                    className="w-full px-4 py-3 rounded-2xl border text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-pink-500 bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-700 text-slate-900 dark:text-white"
                  />
                </div>

                <button
                  type="submit"
                  className="w-full py-4 mt-2 rounded-2xl bg-gradient-to-r from-pink-500 via-purple-500 to-indigo-600 hover:from-pink-600 hover:to-indigo-700 text-white font-black text-base shadow-lg shadow-pink-500/30 flex items-center justify-center gap-2 transition-all hover:scale-[1.02] active:scale-[0.98] cursor-pointer"
                >
                  <span>Let's Bash the Piñata!</span>
                  <SafeIcon icon={FiArrowRight} />
                </button>
              </form>
            </motion.div>
          )}

          {step === 'game' && (
            <motion.div
              key="game"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="w-full"
            >
              <div className="text-center mb-2">
                <span className="text-xs font-black uppercase tracking-wider text-purple-600 bg-purple-100 dark:bg-purple-950 px-3 py-1 rounded-full">
                  Player: {formData.name}
                </span>
              </div>
              <PinataGame onComplete={handleGameComplete} reward={wonPrize} />
            </motion.div>
          )}

          {step === 'result' && (
            <motion.div
              key="result"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              className={`w-full p-8 rounded-3xl border text-center shadow-2xl ${
                isDarkMode ? 'bg-slate-900 border-amber-500/40' : 'bg-white border-pink-200 shadow-pink-200/50'
              }`}
            >
              <div className="w-20 h-20 rounded-full bg-gradient-to-tr from-amber-400 to-pink-500 text-white flex items-center justify-center text-4xl mx-auto mb-4 shadow-xl">
                <SafeIcon icon={FiAward} />
              </div>

              <div className="text-xs font-black uppercase text-pink-600 tracking-widest mb-1">
                🎉 Congratulations!
              </div>

              <h2 className="text-2xl sm:text-3xl font-black text-slate-900 dark:text-white mb-2">
                {formData.name}
              </h2>

              <div className="my-6 p-4 rounded-2xl bg-amber-50 dark:bg-amber-950/30 border border-amber-300 dark:border-amber-700/50">
                <p className="text-xs text-amber-800 dark:text-amber-300 font-bold uppercase tracking-wider mb-1">
                  🎁 Prize Won
                </p>
                <p className="text-xl sm:text-2xl font-black text-slate-900 dark:text-white">
                  {wonPrize}
                </p>
              </div>

              <div className="p-3 bg-slate-100 dark:bg-slate-800 rounded-2xl inline-block mb-6 border border-slate-300 dark:border-slate-700">
                <p className="text-[10px] text-slate-400 uppercase font-black tracking-widest">
                  Event Voucher Code
                </p>
                <p className="text-lg font-mono font-black text-indigo-600 dark:text-indigo-400">
                  {couponCode}
                </p>
              </div>

              <div className="flex flex-col sm:flex-row gap-3">
                <button
                  onClick={handlePlayAgain}
                  className="flex-1 py-3.5 rounded-2xl bg-gradient-to-r from-pink-500 to-purple-600 text-white font-black text-sm flex items-center justify-center gap-2 shadow-lg shadow-pink-500/30 hover:scale-[1.02] active:scale-[0.98] transition-all cursor-pointer"
                >
                  <SafeIcon icon={FiRotateCcw} />
                  <span>Next Kid's Turn</span>
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </main>

      {/* Footer Info */}
      <footer className="text-center text-xs text-slate-400 py-2 border-t border-pink-100 dark:border-slate-800">
        School & Event Carnival Stall • Designed for Smooth Touch Tablet Play
      </footer>
    </div>
  );
};

export default PinataFlow;
